/**
 * Game Engine Abstraction Types
 * Provides a unified interface for different game types
 * Game types are now dynamically registered via GamePluginRegistry
 */

import { Prize } from './prize.types';

/**
 * Game type identifier (e.g., 'wheel', 'scratchcard', 'game-board')
 * This is now an open string type since game types are plugin-based
 * and registered dynamically at runtime via GamePluginRegistry.
 */
export type GameType = string;

export interface GameEngine {
  init(container: HTMLElement, config: GameConfig): Promise<void>;
  start(): void;
  finish(): void;
  destroy(): void;
  getProgress(): number;
}

export interface GameConfig {
  type: GameType;
  prizes: Prize[];
  branding: GameBrandingConfig;
  options: WheelGameOptions | ScratchcardGameOptions;
}

export interface GameBrandingConfig {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
  backgroundImage?: string;
}

// Short Code Configuration (new structure)
// Phase 3: Each short code now has its own game type for multi-game campaigns
export interface ShortCodeConfig {
  id: string;                    // Globally unique ID for URL shortener
  name: string;                  // User-friendly name for this configuration
  gameType: GameType;            // 🆕 Game type for this short code (wheel, scratchcard, etc.)
  gameSettings: GameSettings;    // Visual/gameplay settings specific to game type
  arSettings?: ARSettings;       // AR marker detection and tracking settings
  isActive: boolean;             // Whether this short code is active

  // Messaging controls (per short code/link)
  messagingEnabled?: {
    email?: boolean;             // Enable/disable email for this link (default: true)
    sms?: boolean;               // Enable/disable SMS for this link (requires campaign & client SMS enabled)
  };

  createdAt?: Date;              // Made optional for form creation
  updatedAt?: Date;              // Made optional for form creation

  // Runtime flags (not saved to database)
  arPatternNeedsRegeneration?: boolean;  // Flag to trigger pattern regeneration on save
}

// AR Settings for marker detection and tracking
export interface ARSettings {
  enabled?: boolean;             // Whether AR is enabled for this short code (default: true for backwards compatibility)
  customMarker?: boolean;        // If true, use custom patternData (disables auto-generation)
  patternRatio: number;          // Border/pattern ratio (0.5-0.95, typically 0.9) - ArToolkitContext option
  size?: number;                 // Marker size in meters (internal AR.js use, always 1) - ArMarkerControls option
  sceneScale: number;            // Scene scale multiplier (0.01-1, typically 0.05) - Applied to Three.js scene for size control
  smoothCount: number;           // Number of frames to smooth (5-15, typically 10) - ArMarkerControls option
  smoothThreshold: number;       // Threshold for smoothing (2-10, typically 5) - ArMarkerControls option
  smoothTolerance: number;       // Position tolerance (0.001-0.1, typically 0.01) - ArMarkerControls option

  // AR Marker Pattern Data (stored directly in database for fast access)
  patternData?: string;          // The .patt file content (AR.js marker pattern)
  patternGeneratedAt?: Date;     // Timestamp when pattern was generated
  patternVersion?: number;       // Version number for regeneration tracking (default: 1)

  // 🆕 Phase 3: Print settings for QR code generation
  markerSize?: number;           // Physical marker size in millimeters (default: 150mm)
  printQuality?: {
    dpi: number;                 // Dots per inch for print quality (default: 300)
    notes?: string;              // Installation/print instructions
  };
}

// Default AR Settings (production-tested values)
// All values align with AR.js ArToolkitContext and ArMarkerControls options
export const DEFAULT_AR_SETTINGS: ARSettings = {
  patternRatio: 0.9,
  sceneScale: 0.05,
  smoothCount: 10,
  smoothThreshold: 5,
  smoothTolerance: 0.01
};

// Game Settings by Type
export type GameSettings = WheelGameSettings | ScratchcardGameSettings;

export interface BaseGameSettings {
  type: GameType;
  enableSoundEffects?: boolean; // Enable/disable sound effects for this game
}

// Wheel size presets
export type WheelSize = 'small' | 'medium' | 'large' | 'xlarge';

// Wheel size to pixel mapping
export const WHEEL_SIZE_PIXELS: Record<WheelSize, number> = {
  small: 512,
  medium: 768,
  large: 1024,
  xlarge: 1536
};

// Board size presets (matches wheel sizes for consistency)
export type BoardSize = 'small' | 'medium' | 'large';

// Board size to pixel mapping
export const BOARD_SIZE_PIXELS: Record<BoardSize, number> = {
  small: 512,
  medium: 768,
  large: 1024
};

// Wheel-specific settings (enhanced from spin-wheel package research)
export interface WheelGameSettings extends BaseGameSettings {
  type: 'wheel';
  // Container settings
  wheelSize: WheelSize;                  // Preset wheel size (determines resolution/quality)
  // Visual settings
  radius: number;                        // Wheel size (0-1, percent of container)
  borderColor: string;
  borderWidth: number;
  itemLabelColors: string[];
  itemLabelFont?: string;                // Font family for labels (default: 'Codec Pro')
  itemLabelFontSizeMax: number;
  itemLabelRadius: number;               // Label position (0-1, percent from center)
  itemLabelRadiusMax: number;            // Label max width (0-1, percent from center)
  itemLabelAlign: 'left' | 'center' | 'right';
  itemLabelRotation: number;             // Label rotation in degrees
  itemLabelStrokeColor: string;
  itemLabelStrokeWidth: number;
  // Behavior settings
  rotationSpeedMax: number;
  rotationResistance: number;
  isInteractive: boolean;
  // Line and background settings
  lineColor: string;
  lineWidth: number;
  itemBackgroundColors: string[];
  // Image settings
  image?: string;                        // Background image URL for wheel
  overlayImage?: string;                 // Overlay image URL (e.g., pointer/indicator)
  offset?: { w: number; h: number };     // Wheel positioning offset

  // 3D Settings (for AR/3D rendering)
  // Cylinder base settings
  // Note: cylinderRadius is auto-calculated from wheelSize (5% larger than wheel)
  cylinderHeight?: number;               // Height/thickness of cylinder (default: 0.25)
  cylinderColor?: string;                // Color of cylinder (default: '#ed1f24')
  cylinderOpacity?: number;              // Opacity of cylinder (0-1, default: 0.8)
  cylinderRoughness?: number;            // Material roughness (0=shiny, 1=matte, default: 0.5)
  cylinderMetalness?: number;            // Material metalness (0=non-metal, 1=metal, default: 0.0)
  cylinderTexture?: string;              // Optional texture URL for cylinder

  // Arrow pointer settings
  arrowColor?: string;                   // Color of arrow pointer (default: '#222')
  arrowWidth?: number;                   // Width of arrow at base (default: 0.64)
  arrowHeight?: number;                  // Height of arrow (how far it extends) (default: 0.32)
  arrowDepth?: number;                   // Thickness/depth of arrow (default: 0.05)
  arrowPosition?: 'top' | 'bottom' | 'left' | 'right';  // Position of arrow (default: 'top')

  // Overall 3D scene settings
  scale?: number;                        // Overall scale factor for 3D scene (default: 2.0)
  castShadow?: boolean;                  // Whether 3D elements cast shadows (default: true)
  receiveShadow?: boolean;               // Whether 3D elements receive shadows (default: false)
}

// Scratchcard-specific settings
export interface ScratchcardGameSettings extends BaseGameSettings {
  type: 'scratchcard';
  scratchType: 'brush' | 'spray' | 'circle' | 'line';
  containerWidth: number;
  containerHeight: number;
  imageForwardSrc: string;
  imageBackgroundSrc?: string; // Made optional since we now use solid colors
  htmlBackground?: string;
  clearZoneRadius: number;
  nPoints: number;
  pointSize: number;
  percentToFinish: number;
  brushSrc?: string;
  // Grid-specific options
  gridMode?: boolean;
  gridSpacing?: number; // Spacing between grid squares in pixels
  squareSize?: number; // Size of each grid square
  cardBackgroundColor?: string; // Background color of the HTML card area (legacy/grid mode)
  cardBackgroundImage?: string; // Background image URL for the HTML card area
  prizeBackgroundColor?: string; // Background color for prize squares

  // ============================================================================
  // 3D Card Properties (Physical lottery ticket appearance)
  // ============================================================================

  // Card dimensions
  cardDepth?: number;              // Depth/thickness of card (default: 0.1 units)
  cardMarginPercent?: number;      // Margin around scratchcard as % (default: 0.2 = 20% total)

  // Card appearance (3D mesh)
  card3DColor?: string;            // 3D card base color (default: '#ffffff')
  cardBorderColor?: string;        // Card border/rim color (default: '#222222')
  cardRoughness?: number;          // Material roughness 0-1 (default: 0.3)
  cardMetalness?: number;          // Material metalness 0-1 (default: 0.1)
  cardOpacity?: number;            // Card opacity 0-1 (default: 1.0)

  // Header section (branding area)
  cardHeaderEnabled?: boolean;          // Show header section (default: true)
  cardHeaderHeightPercent?: number;     // Header height as % of card (default: 0.15 = 15%)
  cardHeaderColor?: string;             // Header background color (default: '#ed1f24')
  cardHeaderImageUrl?: string;          // Custom header image URL (optional)
  cardHeaderText?: string;              // Header text (client name/brand)

  // Footer section (info area)
  cardFooterEnabled?: boolean;          // Show footer section (default: false)
  cardFooterHeightPercent?: number;     // Footer height as % of card (default: 0.1 = 10%)
  cardFooterColor?: string;             // Footer background color (default: '#333333')

  // Shadow settings
  castShadow?: boolean;            // Enable shadow casting (default: true)
  receiveShadow?: boolean;         // Enable shadow receiving (default: true)
}

// Legacy interfaces (keeping for backward compatibility during transition)
export interface WheelGameOptions {
  wheelSegments: number;
  wheelColors: string[];
  spinDuration?: number;
  borderColor?: string;
  borderWidth?: number;
  radius?: number;
  itemLabelColors?: string[];
  itemLabelFontSizeMax?: number;
  rotationSpeedMax?: number;
}

export interface ScratchcardGameOptions {
  scratchType: 'brush' | 'spray' | 'circle' | 'line';
  containerWidth: number;
  containerHeight: number;
  imageForwardSrc: string;
  imageBackgroundSrc: string;
  htmlBackground?: string;
  clearZoneRadius: number;
  nPoints: number;
  pointSize: number;
  percentToFinish: number;
  brushSrc?: string;
  // Grid-specific options
  gridMode?: boolean;
  gridSpacing?: number; // Spacing between grid squares in pixels
  squareSize?: number; // Size of each grid square
}

// Game initialization data for GameService
export interface GameInitData {
  game: unknown; // The actual game instance (Wheel | ScratchCard)
  type: GameType;
  scale: number;
  position: [number, number, number];
}

// Game events
export interface GameEvent {
  type: 'progress' | 'complete' | 'start' | 'tick';
  payload?: Record<string, unknown>;
}

export interface GameEventCallback {
  (event: GameEvent): void;
}
